import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { ThemeToggle, LangToggle } from "@/components/Toggles";
import { stageMeta, stageLabel } from "@/lib/constants";
import { Droplets, Boxes, Scale, CheckCircle2, Loader2, MapPin, Hash } from "lucide-react";

export default function Track() {
  const { orderCode } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/track/${orderCode}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [orderCode]);

  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">{t("order_not_found")}</div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const { order } = data;
  const stages = data.stages || [];
  const currentIdx = stages.findIndex((s) => s.id === order.status);
  const lastStage = stages[stages.length - 1]?.id;

  return (
    <div className="min-h-screen bg-background grain-bg">
      <header className="flex items-center justify-between border-b border-border px-4 py-4 sm:px-6">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Droplets className="h-5 w-5" /></div>
          <span className="font-heading font-bold">e-olive Albania</span>
        </div>
        <div className="flex items-center gap-2"><LangToggle /><ThemeToggle /></div>
      </header>

      <div className="mx-auto max-w-lg px-4 py-8" data-testid="track-page">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">{t("track_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("track_sub")}</p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-olive-light px-3 py-1 font-mono text-sm font-semibold text-olive">
            <Hash className="h-3.5 w-3.5" /> {order.order_code}
          </p>
        </div>

        {/* Position */}
        {data.position && order.status !== lastStage && (
          <div className="mt-6 rounded-2xl border border-amber/40 bg-amber-glow/40 p-5 text-center" data-testid="track-position">
            <p className="text-xs uppercase tracking-wider text-amber-deep">{t("your_position")}</p>
            <p className="font-heading text-5xl font-extrabold text-olive">#{data.position}</p>
          </div>
        )}

        {/* Progress stepper */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-6">
          <p className="mb-5 text-sm font-semibold">{t("current_stage")}</p>
          <div className="space-y-1">
            {stages.map((s, i) => {
              const M = stageMeta(s.id, i);
              const Icon = M.icon;
              const done = i < currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.id} className="flex items-center gap-4" data-testid={`track-stage-${s.id}`}>
                  <div className="flex flex-col items-center">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full transition-colors"
                      style={{ background: done || active ? M.color : "hsl(var(--secondary))", color: done || active ? "#fff" : "hsl(var(--muted-foreground))" }}>
                      {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-4 w-4" />}
                    </div>
                    {i < stages.length - 1 && <div className="h-6 w-0.5" style={{ background: done ? M.color : "hsl(var(--border))" }} />}
                  </div>
                  <span className={`text-sm ${active ? "font-bold" : done ? "font-medium" : "text-muted-foreground"} ${active ? "animate-pulse-soft" : ""}`}>
                    {stageLabel(t, s)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Order details */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-6">
          <p className="mb-4 text-sm font-semibold">{t("order_details")}</p>
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={Boxes} label={t("crates")} value={order.crate_count} />
            <Stat icon={Scale} label={t("total_weight")} value={`${order.total_weight}kg`} />
            {order.show_yield && <Stat icon={Droplets} label={t("est_yield")} value={`${order.estimated_yield}L`} />}
          </div>
          {order.crates?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {order.crates.map((c) => (
                <span key={c.number} className="rounded-lg bg-secondary px-2.5 py-1 font-mono text-xs">#{c.number} · {c.weight}kg</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3 text-center">
      <Icon className="mx-auto h-5 w-5 text-olive" />
      <p className="mt-1.5 font-heading text-lg font-bold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}
